import React, { useState, useEffect, useCallback } from 'react';
import {
  getAllPlaceholderTags,
  categorizeTags,
  getDocumentAsBase64,
  getDocumentAsBlob,
  createNewDocumentFromBase64,
} from '../services/wordHelper';
import { getAuthHeaders, API_URL as API_BASE } from '../services/auth';
import './PreviewPanel.css';

/* ── Catalog types ── */
interface CatalogEntry {
  Key: string;
  Type: string;
  Required: boolean;
  SampleValue: string;
  Format?: string;
  IsCalculated?: boolean;
  Expression?: string | null;
}

/* ── Field models ── */
interface SimpleField {
  kind: 'simple';
  tag: string;
  value: string;
  catalogType: string;
  required: boolean;
  sampleValue: string;
  format?: string;
  /** True when this placeholder is derived from an expression (read-only in UI). */
  isCalculated?: boolean;
  /** Math/text expression evaluated server-side using sibling field values. */
  expression?: string;
  /** Last-evaluated calculated value, mirrored from /calc-preview. */
  calcValue?: string;
  /** Last error from /calc-preview, if any. */
  calcError?: string;
}

interface ConditionalField {
  kind: 'conditional';
  tag: string;
  prefix: 'IF' | 'UNLESS';
  key: string;
  enabled: boolean;
}

interface RepeatRowField {
  key: string;
  value: string;
}

interface RepeatField {
  kind: 'repeat';
  tag: string;
  key: string;
  /** Each row is a map of sub-field key -> value */
  rows: RepeatRowField[][];
  subKeys: string[];
}

type FieldEntry = SimpleField | ConditionalField | RepeatField;

/* ── Helpers ── */
async function apiFetch<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`, {
    ...init,
    headers: {
      'Content-Type': 'application/json',
      ...getAuthHeaders(),
      ...(init?.headers || {}),
    },
  });
  if (!res.ok) throw new Error(`API ${res.status}: ${res.statusText}`);
  return res.json();
}

function inputTypeFor(catalogType: string): string {
  switch (catalogType?.toLowerCase()) {
    case 'date':
      return 'date';
    case 'number':
    case 'money':
      return 'number';
    default:
      return 'text';
  }
}

function formatHintFor(catalogType: string): string | undefined {
  switch (catalogType?.toLowerCase()) {
    case 'money':
      return '$#,##0.00';
    case 'number':
      return '0';
    case 'date':
      return 'MMMM d, yyyy';
    default:
      return undefined;
  }
}

/* ── Component ── */
export function PreviewPanel() {
  const [fields, setFields] = useState<FieldEntry[]>([]);
  const [scanning, setScanning] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [previewFormat, setPreviewFormat] = useState<'docx' | 'pdf' | null>(null);
  const [message, setMessage] = useState<{ text: string; type: 'info' | 'error' | 'success' } | null>(null);

  /* ── Scan document + fetch catalog ── */
  const scanDocument = useCallback(async () => {
    setScanning(true);
    setMessage(null);
    try {
      // Include content controls AND plain-text / floating text box tokens.
      const controls = await getAllPlaceholderTags();
      const uniqueTags = [...new Set(controls.map((c) => c.tag))].sort();
      const categorized = categorizeTags(uniqueTags);


      // Fetch placeholder catalog
      let catalogMap = new Map<string, CatalogEntry>();
      try {
        const response = await apiFetch<{ data: CatalogEntry[] }>('/placeholders?limit=500');
        const items = Array.isArray(response) ? response : response.data || [];
        for (const p of items as CatalogEntry[]) {
          if (p.Key) catalogMap.set(p.Key, p);
        }
      } catch {
        /* catalog unavailable — continue without */
      }

      const entries: FieldEntry[] = [];

      // Simple placeholders
      for (const tag of categorized.simple) {
        const info = catalogMap.get(tag);
        entries.push({
          kind: 'simple',
          tag,
          value: '',
          catalogType: info?.Type || 'Text',
          required: info?.Required || false,
          sampleValue: info?.SampleValue || '',
          format: info?.Format,
          isCalculated: !!info?.IsCalculated,
          expression: info?.Expression || undefined,
        });
      }

      // Conditional tags (IF: / UNLESS:)
      for (const tag of categorized.conditional) {
        const isUnless = tag.startsWith('UNLESS:');
        const key = isUnless ? tag.substring(7) : tag.substring(3);
        entries.push({
          kind: 'conditional',
          tag,
          prefix: isUnless ? 'UNLESS' : 'IF',
          key,
          enabled: true,
        });
      }

      // Repeat tags
      for (const tag of categorized.repeat) {
        const key = tag.startsWith('REPEAT:') ? tag.substring(7) : tag;
        // Try to infer sub-keys from catalog entries that start with the repeat key
        const subKeys: string[] = [];
        for (const [k] of catalogMap) {
          if (k.startsWith(`${key}.`)) {
            subKeys.push(k.substring(key.length + 1));
          }
        }
        if (subKeys.length === 0) subKeys.push('value');

        entries.push({
          kind: 'repeat',
          tag,
          key,
          rows: [],
          subKeys,
        });
      }

      setFields(entries);
      setMessage({
        text: `Found ${entries.length} placeholder${entries.length !== 1 ? 's' : ''} in document`,
        type: 'success',
      });
    } catch (err) {
      setMessage({ text: 'Failed to scan document. Is a document open?', type: 'error' });
    } finally {
      setScanning(false);
    }
  }, []);

  // Scan on mount
  useEffect(() => {
    scanDocument();
  }, [scanDocument]);

  /* ── Re-evaluate calculated fields whenever inputs change ──
   * Coerces each non-calculated value to its catalog type, sends the bag to
   * /placeholders/calc-preview, and writes the result back to each calculated
   * field. Debounced 300ms so typing doesn't fire one request per keystroke. */
  useEffect(() => {
    const calcFields = fields.filter(
      (f): f is SimpleField => f.kind === 'simple' && !!f.isCalculated && !!f.expression,
    );
    if (calcFields.length === 0) return;

    // Build samples bag from non-calculated simple fields. Coerce numbers/booleans
    // so the expression parser sees them as the right type.
    const samples: Record<string, unknown> = {};
    for (const f of fields) {
      if (f.kind !== 'simple' || f.isCalculated) continue;
      if (f.value === '') continue;
      const t = f.catalogType.toLowerCase();
      if (t === 'number' || t === 'money') {
        const n = Number(f.value);
        samples[f.tag] = Number.isFinite(n) ? n : f.value;
      } else if (t === 'boolean') {
        samples[f.tag] = f.value === 'true';
      } else {
        samples[f.tag] = f.value;
      }
    }

    let cancelled = false;
    const handle = setTimeout(async () => {
      const results = await Promise.all(
        calcFields.map(async (f) => {
          try {
            const resp = await apiFetch<{ data: { value: unknown; error: string | null } }>(
              '/placeholders/calc-preview',
              {
                method: 'POST',
                body: JSON.stringify({ expression: f.expression, samples }),
              },
            );
            return { tag: f.tag, value: resp.data?.value, error: resp.data?.error || null };
          } catch (err) {
            return {
              tag: f.tag,
              value: null,
              error: err instanceof Error ? err.message : 'Preview failed',
            };
          }
        }),
      );
      if (cancelled) return;
      setFields((prev) =>
        prev.map((f) => {
          if (f.kind !== 'simple' || !f.isCalculated) return f;
          const r = results.find((x) => x.tag === f.tag);
          if (!r) return f;
          const valueStr =
            r.value === null || r.value === undefined ? '' : String(r.value);
          return { ...f, calcValue: valueStr, calcError: r.error || undefined };
        }),
      );
    }, 300);

    return () => {
      cancelled = true;
      clearTimeout(handle);
    };
    // We intentionally re-run when any field changes — the dep array is the
    // serialized non-calculated values, kept stable so calc-only updates don't
    // re-trigger themselves.
  }, [
    // eslint-disable-next-line react-hooks/exhaustive-deps
    fields
      .filter((f) => f.kind === 'simple' && !f.isCalculated)
      .map((f) => `${(f as SimpleField).tag}=${(f as SimpleField).value}`)
      .join('|'),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    fields
      .filter((f) => f.kind === 'simple' && (f as SimpleField).isCalculated)
      .map((f) => `${(f as SimpleField).tag}:${(f as SimpleField).expression || ''}`)
      .join('|'),
  ]);

  /* ── Field update helpers ── */
  const updateSimpleValue = (index: number, value: string) => {
    setFields((prev) => {
      const next = [...prev];
      const f = next[index] as SimpleField;
      next[index] = { ...f, value };
      return next;
    });
  };

  const toggleConditional = (index: number) => {
    setFields((prev) => {
      const next = [...prev];
      const f = next[index] as ConditionalField;
      next[index] = { ...f, enabled: !f.enabled };
      return next;
    });
  };

  const addRepeatRow = (index: number) => {
    setFields((prev) => {
      const next = [...prev];
      const f = next[index] as RepeatField;
      const newRow: RepeatRowField[] = f.subKeys.map((k) => ({ key: k, value: '' }));
      next[index] = { ...f, rows: [...f.rows, newRow] };
      return next;
    });
  };

  const removeRepeatRow = (fieldIndex: number, rowIndex: number) => {
    setFields((prev) => {
      const next = [...prev];
      const f = next[fieldIndex] as RepeatField;
      const rows = [...f.rows];
      rows.splice(rowIndex, 1);
      next[fieldIndex] = { ...f, rows };
      return next;
    });
  };

  const updateRepeatCell = (fieldIndex: number, rowIndex: number, cellIndex: number, value: string) => {
    setFields((prev) => {
      const next = [...prev];
      const f = next[fieldIndex] as RepeatField;
      const rows = f.rows.map((r, ri) =>
        ri === rowIndex ? r.map((c, ci) => (ci === cellIndex ? { ...c, value } : c)) : r,
      );
      next[fieldIndex] = { ...f, rows };
      return next;
    });
  };

  /* ── Auto-fill from catalog sample values ── */
  const autoFill = () => {
    setFields((prev) =>
      prev.map((f) => {
        // Calculated fields are derived — leave their value alone (the calc
        // effect will recompute once sibling values are filled).
        if (f.kind === 'simple' && !f.isCalculated && f.sampleValue) {
          return { ...f, value: f.sampleValue };
        }
        if (f.kind === 'conditional') {
          return { ...f, enabled: true };
        }
        return f;
      }),
    );
    setMessage({ text: 'Sample values applied', type: 'info' });
  };

  /* ── Clear all fields ── */
  const clearAll = () => {
    setFields((prev) =>
      prev.map((f) => {
        if (f.kind === 'simple') return { ...f, value: '' };
        if (f.kind === 'conditional') return { ...f, enabled: true };
        if (f.kind === 'repeat') return { ...f, rows: [] };
        return f;
      }),
    );
    setMessage(null);
  };

  /* ── Build payload ── */
  const buildPayload = (): { placeholders: Record<string, unknown>; formatOptions: Record<string, { Type: string; Format: string }> } => {
    const placeholders: Record<string, unknown> = {};
    // Keys PascalCase because the C# FormatOption model is case-sensitive on
    // deserialization — lowercase `type`/`format` silently map to nothing and
    // the filler runs with no format applied.
    const formatOptions: Record<string, { Type: string; Format: string }> = {};

    for (const f of fields) {
      if (f.kind === 'simple') {
        // Calculated fields always send their evaluated value so the doc-service
        // fills the placeholder — even though the user can't type one in.
        const effectiveValue = f.isCalculated ? (f.calcValue ?? '') : f.value;
        if (effectiveValue !== '') {
          placeholders[f.tag] = effectiveValue;
          const hint = f.format || formatHintFor(f.catalogType);
          if (hint) {
            formatOptions[f.tag] = { Type: f.catalogType.toLowerCase(), Format: hint };
          }
        }
      } else if (f.kind === 'conditional') {
        placeholders[f.tag] = f.enabled;
      } else if (f.kind === 'repeat') {
        if (f.rows.length > 0) {
          placeholders[f.tag] = f.rows.map((row) => {
            const obj: Record<string, string> = {};
            for (const cell of row) {
              obj[cell.key] = cell.value;
            }
            return obj;
          });
        }
      }
    }

    return { placeholders, formatOptions };
  };

  /* ── Preview action ── */
  const handlePreview = async (format: 'docx' | 'pdf') => {
    setPreviewing(true);
    setPreviewFormat(format);
    setMessage({ text: `Generating ${format.toUpperCase()} preview...`, type: 'info' });

    try {
      // Save document first to capture latest edits (required for getFileAsync)
      try {
        await Word.run(async (context) => {
          context.document.save();
          await context.sync();
        });
        // Wait for save to flush to disk
        await new Promise(r => setTimeout(r, 1000));
      } catch {
        // Save failed — proceed anyway, but file may be stale
      }

      // Get document as blob and convert to proper base64
      const blob = await getDocumentAsBlob();
      const base64Doc = await new Promise<string>((resolve) => {
        const reader = new FileReader();
        reader.onloadend = () => {
          const result = reader.result as string;
          resolve(result.split(',')[1]);
        };
        reader.readAsDataURL(blob);
      });

      const { placeholders } = buildPayload();

      // Call the single /resolve endpoint to apply ALL rules (defaults, transforms, formatting, validation, constraints)
      let resolvedPlaceholders = placeholders;
      let formatOptions: Record<string, { type: string; format: string }> = {};
      let warnings: any[] = [];

      try {
        const resolveResp = await fetch(`${API_BASE}/placeholder-rules/resolve`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
          body: JSON.stringify({ placeholders }),
        });
        const resolveData = await resolveResp.json();
        if (resolveData.success && resolveData.data) {
          resolvedPlaceholders = resolveData.data.values || placeholders;
          formatOptions = resolveData.data.formatOptions || {};
          warnings = resolveData.data.warnings || [];
        }
      } catch {
        // Resolve failed — proceed with raw values
      }

      if (warnings.length > 0) {
        console.log('[Preview] Warnings:', warnings);
      }

      // Route through template-service /preview so fragment chips are expanded
      // with current library content before placeholder filling.
      const endpoint = format === 'pdf' ? '/preview/fill-and-convert' : '/preview/fill';

      const response = await fetch(`${API_BASE}${endpoint}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders(),
        },
        body: JSON.stringify({
          docxBuffer: base64Doc,
          placeholders: resolvedPlaceholders,
          formatOptions,
        }),
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Server error ${response.status}: ${errorText}`);
      }

      const result = await response.json();
      const resultBase64: string = result.data || result.base64 || result.buffer;

      if (!resultBase64) {
        throw new Error('No document data in response');
      }

      if (format === 'docx') {
        // Offer to open in Word or download
        setMessage({ text: 'Preview ready! Choose an action below.', type: 'success' });
        setPreviewResult({ base64: resultBase64, format });
      } else {
        // PDF — download directly
        downloadBlob(resultBase64, `preview.${format}`, format === 'pdf' ? 'application/pdf' : 'application/vnd.openxmlformats-officedocument.wordprocessingml.document');
        setMessage({ text: 'PDF downloaded successfully', type: 'success' });
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Preview generation failed';
      setMessage({ text: msg, type: 'error' });
    } finally {
      setPreviewing(false);
      setPreviewFormat(null);
    }
  };

  /* ── Preview result state (for DOCX open-in-Word / download) ── */
  const [previewResult, setPreviewResult] = useState<{ base64: string; format: 'docx' | 'pdf' } | null>(null);

  const handleOpenInWord = async () => {
    if (!previewResult) return;
    try {
      await createNewDocumentFromBase64(previewResult.base64);
      setMessage({ text: 'Document opened in new Word window', type: 'success' });
      setPreviewResult(null);
    } catch {
      setMessage({ text: 'Failed to open document in Word', type: 'error' });
    }
  };

  const handleDownload = () => {
    if (!previewResult) return;
    const mime =
      previewResult.format === 'pdf'
        ? 'application/pdf'
        : 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
    downloadBlob(previewResult.base64, `preview.${previewResult.format}`, mime);
    setPreviewResult(null);
    setMessage({ text: 'File downloaded', type: 'success' });
  };

  /* ── Download helper ── */
  function downloadBlob(base64: string, filename: string, mimeType: string) {
    const byteChars = atob(base64);
    const byteNumbers = new Array(byteChars.length);
    for (let i = 0; i < byteChars.length; i++) {
      byteNumbers[i] = byteChars.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers);
    const blob = new Blob([byteArray], { type: mimeType });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }

  /* ── Derived state ── */
  const missingRequired = fields.filter(
    (f) => f.kind === 'simple' && f.required && !f.isCalculated && !f.value,
  );
  const hasFields = fields.length > 0;

  /* ── Render ── */
  return (
    <div className="dp-preview">
      {/* Header */}
      <div className="dp-preview__header">
        <h3>Preview &amp; Test</h3>
        <button onClick={scanDocument} disabled={scanning} className="dp-preview__scan-btn">
          {scanning ? 'Scanning...' : 'Re-scan'}
        </button>
      </div>

      {/* Fields area */}
      <div className="dp-preview__fields">
        {!hasFields && !scanning && (
          <div className="dp-preview__empty">
            No placeholders found. Open a document with content controls, then click Re-scan.
          </div>
        )}

        {scanning && (
          <div className="dp-preview__loading">
            <div className="dp-preview__spinner" />
            Scanning document...
          </div>
        )}

        {missingRequired.length > 0 && (
          <div className="dp-preview__warning">
            {missingRequired.length} required field{missingRequired.length !== 1 ? 's' : ''} missing a value
          </div>
        )}

        {/* Simple fields */}
        {fields.filter((f): f is SimpleField => f.kind === 'simple').length > 0 && (
          <div className="dp-preview__section">
            <div className="dp-preview__section-title">Placeholders</div>
            {fields.map((f, i) => {
              if (f.kind !== 'simple') return null;
              return (
                <div
                  key={f.tag}
                  className={`dp-preview__field ${f.required && !f.value ? 'dp-preview__field--error' : ''}`}
                >
                  <div className="dp-preview__field-label">
                    <span className="dp-preview__field-name">{f.tag}</span>
                    <span className={`dp-preview__field-type dp-preview__field-type--${f.catalogType.toLowerCase()}`}>
                      {f.catalogType}
                    </span>
                    {f.required && <span className="dp-preview__field-required">*</span>}
                  </div>
                  {f.isCalculated ? (
                    <div className="dp-preview__calc">
                      <input
                        type="text"
                        className="dp-preview__input"
                        value={f.calcError ? '—' : (f.calcValue ?? '')}
                        readOnly
                        placeholder="(calculated)"
                        style={{ background: '#f5f5f5', cursor: 'not-allowed' }}
                      />
                      <div className="dp-preview__format-hint" style={{ fontFamily: 'monospace' }}>
                        ƒ {f.expression}
                      </div>
                      {f.calcError && (
                        <div className="dp-preview__format-hint" style={{ color: '#b54708' }}>
                          {f.calcError}
                        </div>
                      )}
                    </div>
                  ) : f.catalogType.toLowerCase() === 'boolean' ? (
                    <label className="dp-preview__checkbox">
                      <input
                        type="checkbox"
                        checked={f.value === 'true'}
                        onChange={(e) => updateSimpleValue(i, e.target.checked ? 'true' : 'false')}
                      />
                      <span>{f.value === 'true' ? 'Yes' : 'No'}</span>
                    </label>
                  ) : f.catalogType.toLowerCase() === 'list' ? (
                    <textarea
                      className="dp-preview__textarea"
                      value={f.value}
                      onChange={(e) => updateSimpleValue(i, e.target.value)}
                      placeholder='["item1", "item2"]'
                      rows={2}
                    />
                  ) : (
                    <input
                      type={inputTypeFor(f.catalogType)}
                      className="dp-preview__input"
                      value={f.value}
                      onChange={(e) => updateSimpleValue(i, e.target.value)}
                      placeholder={f.sampleValue || `Enter ${f.tag}`}
                      step={f.catalogType.toLowerCase() === 'money' ? '0.01' : undefined}
                    />
                  )}
                  {!f.isCalculated && formatHintFor(f.catalogType) && (
                    <span className="dp-preview__format-hint">Format: {formatHintFor(f.catalogType)}</span>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Conditional fields */}
        {fields.filter((f): f is ConditionalField => f.kind === 'conditional').length > 0 && (
          <div className="dp-preview__section">
            <div className="dp-preview__section-title">Conditionals</div>
            {fields.map((f, i) => {
              if (f.kind !== 'conditional') return null;
              return (
                <div key={f.tag} className="dp-preview__field dp-preview__field--conditional">
                  <div className="dp-preview__field-label">
                    <span className={`dp-preview__cond-badge dp-preview__cond-badge--${f.prefix.toLowerCase()}`}>
                      {f.prefix}
                    </span>
                    <span className="dp-preview__field-name">{f.key}</span>
                  </div>
                  <label className="dp-preview__toggle">
                    <input
                      type="checkbox"
                      checked={f.enabled}
                      onChange={() => toggleConditional(i)}
                    />
                    <span className="dp-preview__toggle-slider" />
                    <span className="dp-preview__toggle-label">{f.enabled ? 'ON' : 'OFF'}</span>
                  </label>
                </div>
              );
            })}
          </div>
        )}

        {/* Repeat fields */}
        {fields.filter((f): f is RepeatField => f.kind === 'repeat').length > 0 && (
          <div className="dp-preview__section">
            <div className="dp-preview__section-title">Repeating Sections</div>
            {fields.map((f, fi) => {
              if (f.kind !== 'repeat') return null;
              return (
                <div key={f.tag} className="dp-preview__field dp-preview__repeat">
                  <div className="dp-preview__field-label">
                    <span className="dp-preview__repeat-badge">REPEAT</span>
                    <span className="dp-preview__field-name">{f.key}</span>
                  </div>
                  {f.rows.map((row, ri) => (
                    <div key={ri} className="dp-preview__repeat-row">
                      <span className="dp-preview__repeat-row-num">#{ri + 1}</span>
                      <div className="dp-preview__repeat-row-fields">
                        {row.map((cell, ci) => (
                          <div key={cell.key} className="dp-preview__repeat-cell">
                            <label className="dp-preview__repeat-cell-label">{cell.key}</label>
                            <input
                              type="text"
                              className="dp-preview__input"
                              value={cell.value}
                              onChange={(e) => updateRepeatCell(fi, ri, ci, e.target.value)}
                              placeholder={cell.key}
                            />
                          </div>
                        ))}
                      </div>
                      <button
                        className="dp-preview__repeat-remove"
                        onClick={() => removeRepeatRow(fi, ri)}
                        title="Remove row"
                      >
                        x
                      </button>
                    </div>
                  ))}
                  <button className="dp-preview__repeat-add" onClick={() => addRepeatRow(fi)}>
                    + Add Row
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Preview result actions */}
      {previewResult && (
        <div className="dp-preview__result-bar">
          <span className="dp-preview__result-text">Preview ready</span>
          <button className="dp-preview__result-btn dp-preview__result-btn--primary" onClick={handleOpenInWord}>
            Open in Word
          </button>
          <button className="dp-preview__result-btn" onClick={handleDownload}>
            Download
          </button>
          <button className="dp-preview__result-btn dp-preview__result-btn--dismiss" onClick={() => setPreviewResult(null)}>
            Dismiss
          </button>
        </div>
      )}

      {/* Message */}
      {message && (
        <div className={`dp-preview__message dp-preview__message--${message.type}`}>
          {message.text}
        </div>
      )}

      {/* Progress indicator */}
      {previewing && (
        <div className="dp-preview__progress">
          <div className="dp-preview__progress-bar" />
          <span>Generating {previewFormat?.toUpperCase()}...</span>
        </div>
      )}

      {/* Sticky action bar */}
      <div className="dp-preview__actions">
        <button
          className="dp-preview__action-btn dp-preview__action-btn--primary"
          onClick={() => handlePreview('docx')}
          disabled={!hasFields || previewing}
        >
          {previewing && previewFormat === 'docx' ? 'Generating...' : 'Preview DOCX'}
        </button>
        <button
          className="dp-preview__action-btn dp-preview__action-btn--secondary"
          onClick={() => handlePreview('pdf')}
          disabled={!hasFields || previewing}
        >
          {previewing && previewFormat === 'pdf' ? 'Generating...' : 'Preview PDF'}
        </button>
        <button
          className="dp-preview__action-btn dp-preview__action-btn--outline"
          onClick={autoFill}
          disabled={!hasFields}
        >
          Auto-fill
        </button>
        <button
          className="dp-preview__action-btn dp-preview__action-btn--ghost"
          onClick={clearAll}
          disabled={!hasFields}
        >
          Clear
        </button>
      </div>
    </div>
  );
}
